import { Routes, Route } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import { publicRoutes, privateRoutes } from "../src/routes";
import "./App.css";
import { useDispatch, useSelector } from "react-redux";
import DefaultLayout from "./layouts/DefaultLayout/DefaultLayout";
import { createTheme, ThemeProvider } from "@mui/material";
import { userLogoutSuccess, userSigninSuccess } from "./redux/userSlice";
import {
  clearAuthStorage,
  getAccessToken,
  hasUsableSession,
  isTokenExpired,
  refreshCurrentUser,
} from "./utils/authSession";

const theme = createTheme({
  palette: {
    primary: {
      main: "#46a8e8",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#4fbf9f",
      contrastText: "#0b1e30",
    },
    text: {
      primary: "#132b3f",
      secondary: "#6f8798",
    },
    background: {
      default: "#f4fbff",
      paper: "#fffefd",
    },
  },
  shape: {
    borderRadius: 16,
  },
  typography: {
    fontFamily:
      'var(--lingualica-font-sans)',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    body1: { fontWeight: 400, letterSpacing: 0, lineHeight: 1.6 },
    body2: { fontWeight: 400, letterSpacing: 0, lineHeight: 1.55 },
    subtitle1: { fontWeight: 500, letterSpacing: 0 },
    subtitle2: { fontWeight: 500, letterSpacing: 0 },
    caption: { fontWeight: 400, letterSpacing: 0 },
    overline: { fontWeight: 600, letterSpacing: ".08em" },
    h1: { color: "#132b3f", fontWeight: 900, letterSpacing: 0, lineHeight: 1.02 },
    h2: { color: "#132b3f", fontWeight: 800, letterSpacing: 0, lineHeight: 1.08 },
    h3: { color: "#132b3f", fontWeight: 700, letterSpacing: 0, lineHeight: 1.16 },
    h4: { color: "#132b3f", fontWeight: 600, letterSpacing: 0, lineHeight: 1.22 },
    h5: { color: "#132b3f", fontWeight: 600, letterSpacing: 0 },
    h6: { color: "#132b3f", fontWeight: 600, letterSpacing: 0 },
    button: { fontWeight: 600, textTransform: "none", letterSpacing: 0 },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          letterSpacing: 0,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          boxShadow: "none",
          fontWeight: 600,
          transition:
            "transform 180ms cubic-bezier(.32,.72,0,1), background-color 180ms ease, box-shadow 180ms ease",
          "&:hover": {
            boxShadow: "0 14px 26px -20px rgba(35, 135, 204, 0.58)",
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        },
        containedSecondary: {
          color: "#0b1e30",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderRadius: 20,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 500,
        },
        label: {
          fontWeight: "inherit",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontWeight: 500,
          letterSpacing: 0,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: "#fffefd",
          "& fieldset": {
            borderColor: "#d7eaf5",
          },
          "&:hover fieldset": {
            borderColor: "#9ed6f4",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#46a8e8",
            borderWidth: 2,
          },
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          padding: "10px 12px",
          borderRadius: 12,
        },
        ul: {
          gap: 6,
          justifyContent: "center",
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          minWidth: 38,
          height: 38,
          fontWeight: 700,
          color: "#07111f",
          border: "1px solid #d7eaf5",
          backgroundColor: "#fffefd",
          "&:hover": {
            backgroundColor: "#e8f6ff",
            borderColor: "#46a8e8",
            color: "#2387cc",
          },
          "&.Mui-selected": {
            backgroundColor: "#46a8e8",
            borderColor: "#46a8e8",
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "#2387cc",
              borderColor: "#2387cc",
            },
          },
        },
        ellipsis: {
          border: "none",
          backgroundColor: "transparent",
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: "#6f8798",
          borderTop: "1px solid #d7eaf5",
          backgroundColor: "#fffefd",
        },
        toolbar: {
          gap: 8,
          flexWrap: "wrap",
        },
        selectLabel: {
          fontWeight: 500,
        },
        displayedRows: {
          fontWeight: 500,
        },
      },
    },
  },
});

const renderRoute = (route, index) => {
  const Page = route.component;
  let Layout = DefaultLayout;

  if (route.layout) {
    Layout = route.layout;
  } else if (route.layout === null) {
    Layout = Fragment;
  }

  return (
    <Route
      key={index}
      path={route.path}
      element={
        <Layout>
          <Page />
        </Layout>
      }
    />
  );
};

function App() {
  const [authChecked, setAuthChecked] = useState(false);
  const currentUser = useSelector((state) => state.user.signin.currentUser);
  const dispatch = useDispatch();
  const isAdmin = currentUser?.metadata?.user?.isAdmin;

  useEffect(() => {
    const handleAuthInvalidated = () => {
      dispatch(userLogoutSuccess());
    };

    window.addEventListener("lingualica:auth-invalidated", handleAuthInvalidated);

    return () => {
      window.removeEventListener("lingualica:auth-invalidated", handleAuthInvalidated);
    };
  }, [dispatch]);

  useEffect(() => {
    let mounted = true;

    const validatePersistedSession = async () => {
      if (!currentUser) {
        setAuthChecked(true);
        return;
      }

      if (!hasUsableSession(currentUser)) {
        clearAuthStorage();
        dispatch(userLogoutSuccess());
        if (mounted) setAuthChecked(true);
        return;
      }

      const accessToken = getAccessToken(currentUser);
      if (!isTokenExpired(accessToken)) {
        setAuthChecked(true);
        return;
      }

      try {
        const refreshedUser = await refreshCurrentUser(currentUser);
        if (refreshedUser) {
          dispatch(userSigninSuccess(refreshedUser));
        } else {
          clearAuthStorage();
          dispatch(userLogoutSuccess());
        }
      } catch (error) {
        clearAuthStorage();
        dispatch(userLogoutSuccess());
      } finally {
        if (mounted) setAuthChecked(true);
      }
    };

    validatePersistedSession();

    return () => {
      mounted = false;
    };
  }, [currentUser, dispatch]);

  if (!authChecked) return null;

  return (
    <ThemeProvider theme={theme}>
      <div className="App">
        <Routes>
          {publicRoutes.map(renderRoute)}
          {currentUser &&
            isAdmin &&
            privateRoutes
              .filter((route) => route.type === "admin")
              .map(renderRoute)}
          {currentUser &&
            !isAdmin &&
            privateRoutes
              .filter((route) => route.type === "user")
              .map(renderRoute)}
        </Routes>
      </div>
    </ThemeProvider>
  );
}

export default App;
